<template>
  <div class="game-builder-page">
    <div class="page-title">
      <div class="page-title-text-area">
        <h1>Game Builder</h1>
      </div>
      <label class="token">
        <span class="form-label">FS token</span>
        <!-- Temporary FamilySearch access token entry -- this whole control will be removed later. -->
        <input type="text" class="form-control" v-model.trim="tokenRef" placeholder="FamilySearch access token" autocomplete="off" spellcheck="false"/>
      </label>
    </div>

    <div class="game-fields">
      <label>
        <span class="form-label">Title</span>
        <input type="text" class="form-control" v-model.trim="titleRef" placeholder="Game title" autocomplete="off"/>
      </label>
      <label>
        <span class="form-label">Subtitle</span>
        <input type="text" class="form-control" v-model.trim="subtitleRef" placeholder="Game subtitle" autocomplete="off"/>
      </label>
    </div>

    <p v-if="errorRef" class="error">{{ errorRef }}</p>
    <LoadingIndicator v-if="loadingRef"/>

    <div v-if="gameIdRef" class="editor">
      <div class="cards">
        <h4>Cards <span v-if="sortedCardsRef.length" class="cards-count">({{ sortedCardsRef.length }})</span></h4>
        <button
          v-for="card in sortedCardsRef"
          :key="card.id"
          type="button"
          class="card-item"
          :class="{ selected: card.id === selectedCardIdRef }"
          @click="selectedCardIdRef = card.id"
        >
          <span class="card-thumb">
            <img v-if="card.images?.length" :src="gamesApi.imageUrl(gameIdRef, card.images[0].imageId)" v-focal="{ focusX: card.images[0].focusX ?? 0.5, focusY: card.images[0].focusY ?? 0.5, zoom: card.images[0].zoom ?? 1 }" alt=""/>
          </span>
          <span class="card-info">
            <span class="card-name">{{ card.name || '(unnamed)' }}</span>
            <span v-if="card.relationship" class="rel-squares">
              <span v-for="(step, i) in relationshipSquares(card.relationship)" :key="i" class="rel-square" :class="step"></span>
            </span>
          </span>
          <span v-if="flagName(card.flag)" class="fi card-flag" :class="`fi-${card.flag}`" :title="flagName(card.flag)"></span>
        </button>
        <button type="button" class="btn btn-link new-card" @click="openNewCard">+ Add card</button>
      </div>

      <div v-if="selectedCardRef" class="card-editor">
        <h4>Card info</h4>
        <div class="person-id">
          <span>Person id</span>
          <span v-if="selectedCardRef.sourcePersonId" class="value">{{ selectedCardRef.sourcePersonId }}</span>
          <input v-else type="text" class="form-control person-id-input" placeholder="Add FamilySearch id, e.g. KWJH-FC3" @change="setPersonId($event)"/>
          <button class="btn btn-link" :disabled="!selectedPersonIdRef || savingRef" @click="readCardFromFamilySearch">Read from FamilySearch</button>
        </div>

        <div class="fields">
          <div class="field-row">
            <span class="field-label form-label">Name</span>
            <input type="text" class="form-control field-name" v-model="selectedCardRef.name"/>
          </div>
          <div class="field-row">
            <span class="field-label form-label">Relationship</span>
            <input type="text" class="form-control field-relationship" v-model="selectedCardRef.relationship"/>
            <span class="field-hint">(FM = father's mother)</span>
          </div>
          <div v-if="selectedCardRef.relationship" class="field-row">
            <span class="field-label"></span>
            <span class="rel-squares">
              <span v-for="(step, i) in relationshipSquares(selectedCardRef.relationship)" :key="i" class="rel-square" :class="step"></span>
            </span>
          </div>
          <div class="field-row">
            <span class="field-label form-label">Text</span>
            <input type="text" class="form-control field-wide" v-model="selectedCardRef.text"/>
          </div>
          <div class="field-row">
            <span class="field-label form-label">Sibling order</span>
            <input type="number" class="form-control field-number" :value="selectedCardRef.childOrder" @input="setNumber('childOrder', $event)"/>
            <span class="field-hint">of</span>
            <input type="number" class="form-control field-number" :value="selectedCardRef.siblingsCount" @input="setNumber('siblingsCount', $event)"/>
          </div>
          <div class="field-row">
            <span class="field-label form-label">Children</span>
            <input type="number" class="form-control field-number" :value="selectedCardRef.childCount" @input="setNumber('childCount', $event)"/>
          </div>
          <div class="field-row">
            <span class="field-label form-label">Flag</span>
            <input type="text" class="form-control field-flag" v-model="selectedCardRef.flag" placeholder="e.g. gb-eng" maxlength="6"/>
            <span v-if="selectedCardRef.flag" class="fi" :class="`fi-${selectedCardRef.flag}`"></span>
            <span v-if="flagCountryNameRef" class="flag-country">{{ flagCountryNameRef }}</span>
          </div>
          <div class="field-row field-notes">
            <span class="field-label form-label">Notes</span>
            <textarea class="form-control field-wide" rows="3" v-model="selectedCardRef.notes"></textarea>
          </div>
        </div>

        <div class="images">
          <div
            v-if="selectedCardRef.images?.length"
            class="tile primary"
            :class="{ selected: selectedCardRef.images[0].imageId === selectedImageIdRef }"
            tabindex="0"
            @pointerdown="onTileDown($event, selectedCardRef.images[0])"
            @pointermove="onTileMove($event, selectedCardRef.images[0])"
            @pointerup="onTileUp($event, selectedCardRef.images[0])"
            @pointercancel="onTileUp($event, selectedCardRef.images[0])"
            @wheel="onTileWheel($event, selectedCardRef.images[0])"
            @dragstart.prevent
          >
            <img :src="gamesApi.imageUrl(gameIdRef, selectedCardRef.images[0].imageId)" v-focal="{ focusX: selectedCardRef.images[0].focusX ?? 0.5, focusY: selectedCardRef.images[0].focusY ?? 0.5, zoom: selectedCardRef.images[0].zoom ?? 1 }" alt="" draggable="false"/>
          </div>
          <button v-else type="button" class="tile add primary" @click="openAddImage"><PlusIcon/></button>

          <div v-if="selectedCardRef.images?.length" class="thumbnails">
            <div
              v-for="image in selectedCardRef.images.slice(1)"
              :key="image.imageId"
              class="tile"
              :class="{ selected: image.imageId === selectedImageIdRef }"
              tabindex="0"
              @pointerdown="onTileDown($event, image)"
              @pointermove="onTileMove($event, image)"
              @pointerup="onTileUp($event, image)"
              @pointercancel="onTileUp($event, image)"
              @wheel="onTileWheel($event, image)"
              @dragstart.prevent
            >
              <img :src="gamesApi.imageUrl(gameIdRef, image.imageId)" v-focal="{ focusX: image.focusX ?? 0.5, focusY: image.focusY ?? 0.5, zoom: image.zoom ?? 1 }" alt="" draggable="false"/>
            </div>
            <button type="button" class="tile add small" @click="openAddImage"><PlusIcon/></button>
          </div>
        </div>

        <div v-if="selectedImageRef" class="image-actions">
          <button type="button" class="btn btn-link" :disabled="selectedImageIndexRef === 0" @click="makeSelectedPrimary">Make primary</button>
          <button type="button" class="btn btn-link" :disabled="selectedImageIndexRef === 0" @click="moveSelectedImage(-1)" aria-label="Move earlier"><ChevronLeftIcon/></button>
          <button type="button" class="btn btn-link" :disabled="selectedImageIndexRef === lastImageIndexRef" @click="moveSelectedImage(1)" aria-label="Move later"><ChevronRightIcon/></button>
        </div>

        <div class="details">
          <div class="details-side">
            <span class="details-label">Details</span>
            <div class="details-actions">
              <button type="button" class="btn btn-link select-toggle" @click="toggleSelectMode">{{ selectModeRef ? 'Done' : 'Select' }}</button>
              <button v-if="selectModeRef" type="button" class="btn btn-link delete-selected" :disabled="!selectedDetailsRef.size" @click="deleteSelectedDetails" aria-label="Delete selected details">
                <Trash2Icon/>
              </button>
            </div>
          </div>
          <div class="details-list">
            <label v-if="selectModeRef" class="select-all">
              <input type="checkbox" :checked="allDetailsSelectedRef" :indeterminate="someDetailsSelectedRef" @change="toggleSelectAll"/>
              <span>Select all</span>
            </label>
            <div
              v-for="(detail, index) in sortedDetailsRef"
              :key="index"
              class="detail"
              :class="{ highlight: detail.highlight }"
            >
              <input v-if="selectModeRef" type="checkbox" class="detail-check" :checked="selectedDetailsRef.has(detail)" @change="toggleDetailSelected(detail)"/>
              <span class="detail-date">{{ formatDate(detail.date) }}</span>
              <span class="detail-age">{{ detailAge(detail) ?? '' }}</span>
              <div class="detail-body">
                <button type="button" class="btn btn-link detail-type" @click="openEditDetail(detail)">{{ detail.type }}</button>
                <span v-if="detail.place" class="detail-place">{{ detail.place }}</span>
              </div>
              <span v-if="detail.flag" class="fi detail-flag" :class="`fi-${detail.flag}`"></span>
            </div>
            <button v-if="!selectModeRef" type="button" class="btn btn-link add-detail" @click="openAddDetail">+ Add detail</button>
          </div>
        </div>

        <div class="danger-zone">
          <button class="btn btn-outline-danger" :disabled="savingRef" @click="deleteSelectedCard">Delete card</button>
        </div>
      </div>
    </div>

    <NewCardModal ref="newCardModalRef" @add="onAddCard"/>
    <EditDetailModal ref="editDetailModalRef" @save="onSaveDetail" @delete="onDeleteDetail"/>
    <ImageGalleryModal ref="imageGalleryModalRef" :images="selectedGalleryRef" :loading="loadingGalleryRef" @select="onSelectImage"/>
    <Modal ref="deleteModalRef" ok-style="danger" ok-text="Delete" @ok="confirmDelete.confirm" @hidden="confirmDelete.dismiss">
      <template #title>Delete card</template>
      <p>Delete this card? This can't be undone.</p>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { debounce } from 'lodash'
import { useGamesApi, GameCardDetail, GameCardInfo, GameCardImage } from './GamesApi'
import { readGameSourcePerson, readGameSourceImages, SourceImage } from './FamilySearchGameSource'
import { vFocal, panFocal, zoomFocalToward } from './vFocal'
import { formatDate, parseDate } from './matching/MatchingGameHelpers'
import { GeoUtil, CountryCodes } from '@/util/GeoUtil'
import NewCardModal from './NewCardModal.vue'
import EditDetailModal from './EditDetailModal.vue'
import ImageGalleryModal from './ImageGalleryModal.vue'
import Modal from '@/util/Modal.vue'
import LoadingIndicator from '@/util/LoadingIndicator.vue'
import { useConfirmation } from '@/util/Confirmation'
import { PlusIcon, Trash2Icon, ChevronLeftIcon, ChevronRightIcon } from '@zhuowenli/vue-feather-icons'

const props = defineProps<{
  gameId?: string
}>()

const gamesApi = useGamesApi()

// Temporary FamilySearch access token, kept in localStorage so it survives a reload -- this
// whole token flow is throwaway authoring scaffolding and will be removed later.
const TOKEN_STORAGE_KEY = 'fsGameToken'
const tokenRef = ref('')

const gameIdRef = ref<string>()
const titleRef = ref('')
const subtitleRef = ref('')
const cardsRef = ref<GameCardInfo[]>([])
const selectedCardIdRef = ref<string>()
const selectedImageIdRef = ref<string>()

// Session-only map -- GameCardInfo has no field for a card's FS memories gallery, so it isn't
// persisted; it's populated when a card is imported, re-read, or the add-image picker is first opened.
const galleryByCardIdRef = ref<Record<string, SourceImage[]>>({})
const loadingGalleryRef = ref(false)

const loadingRef = ref(false)
const savingRef = ref(false)
const errorRef = ref('')

const newCardModalRef = ref<InstanceType<typeof NewCardModal>>()
const editDetailModalRef = ref<InstanceType<typeof EditDetailModal>>()
const editDetailRef = ref<GameCardDetail>()
const imageGalleryModalRef = ref<InstanceType<typeof ImageGalleryModal>>()
const deleteModalRef = ref<InstanceType<typeof Modal>>()
const confirmDelete = useConfirmation(deleteModalRef)

// Cards are shown in name order (case-insensitive, unnamed last).
const sortedCardsRef = computed(() => {
  return [...cardsRef.value].sort((a, b) => {
    const an = a.name?.trim().toLowerCase() ?? ''
    const bn = b.name?.trim().toLowerCase() ?? ''
    if (!an && !bn) {
      return 0
    }
    if (!an) {
      return 1
    }
    if (!bn) {
      return -1
    }
    return an.localeCompare(bn)
  })
})

const selectedCardRef = computed(() => cardsRef.value.find(c => c.id === selectedCardIdRef.value))
const selectedPersonIdRef = computed(() => selectedCardRef.value?.sourcePersonId)
const selectedGalleryRef = computed(() => selectedCardIdRef.value ? (galleryByCardIdRef.value[selectedCardIdRef.value] ?? []) : [])

const selectedImageRef = computed(() => selectedCardRef.value?.images?.find(image => image.imageId === selectedImageIdRef.value))
const selectedImageIndexRef = computed(() => selectedCardRef.value?.images?.findIndex(image => image.imageId === selectedImageIdRef.value) ?? -1)
const lastImageIndexRef = computed(() => (selectedCardRef.value?.images?.length ?? 0) - 1)

// The country name for a flag code, or undefined if it isn't a recognized code.
function flagName(flag?: string) {
  return CountryCodes.toName(flag?.toUpperCase())
}
const flagCountryNameRef = computed(() => flagName(selectedCardRef.value?.flag))

const sortedDetailsRef = computed(() => [...(selectedCardRef.value?.details ?? [])].sort((a, b) => detailSortValue(a) - detailSortValue(b)))

// Bulk detail selection -- FS imports carry a lot of noise (duplicate facts, census rows), so Select
// mode shows a checkbox per detail for multi-delete. Selected by object reference (the list is sorted).
const selectModeRef = ref(false)
const selectedDetailsRef = ref(new Set<GameCardDetail>())
const allDetailsSelectedRef = computed(() => {
  const details = selectedCardRef.value?.details
  return !!details?.length && details.every(detail => selectedDetailsRef.value.has(detail))
})
const someDetailsSelectedRef = computed(() => selectedDetailsRef.value.size > 0 && !allDetailsSelectedRef.value)

onMounted(async () => {
  window.addEventListener('keydown', onKeyDown)
  tokenRef.value = localStorage.getItem(TOKEN_STORAGE_KEY) ?? ''
  if (props.gameId) {
    await loadGame(props.gameId)
  }
})

function persistToken() {
  localStorage.setItem(TOKEN_STORAGE_KEY, tokenRef.value)
}

// Keep the throwaway FS token in localStorage so it survives a reload.
watch(tokenRef, persistToken)

async function loadGame(gameId: string) {
  loadingRef.value = true
  errorRef.value = ''
  try {
    const definition = await gamesApi.getDefinition(gameId)
    titleRef.value = definition?.title ?? ''
    subtitleRef.value = definition?.subtitle ?? ''
    cardsRef.value = await gamesApi.listCardInfo(gameId)
    gameIdRef.value = gameId
    await healAllCards()
    // Select the first card (name order) on load so the editor isn't empty.
    if (!selectedCardIdRef.value && sortedCardsRef.value.length) {
      selectedCardIdRef.value = sortedCardsRef.value[0].id
    }
  } catch (e) {
    errorRef.value = `Couldn't load the game: ${(e as Error).message}`
  } finally {
    loadingRef.value = false
  }
}

async function saveDefinition() {
  const gameId = gameIdRef.value
  if (!gameId) {
    return
  }
  await gamesApi.putDefinition(gameId, {
    id: gameId,
    gameType: 'Matching',
    title: titleRef.value,
    subtitle: subtitleRef.value,
    cards: sortedCardsRef.value.map(c => c.id),
  })
}

// Persist the edited card, debounced. Re-looks-up the card by id at fire time so a since-deleted
// card is a no-op (find returns undefined).
const autosaveCard = debounce((gameId: string, cardId: string) => {
  const card = cardsRef.value.find(c => c.id === cardId)
  if (!card) {
    return
  }
  gamesApi.putCardInfo(gameId, cardId, card).catch(e => { errorRef.value = `Couldn't save the card: ${(e as Error).message}` })
}, 700)

// Deep-watch the selected card: a deep mutation (prev===current, same id) schedules a save; a
// switch (different id) flushes the card we're leaving so its pending edits aren't lost.
watch(selectedCardRef, (card, prev) => {
  if (prev && card && prev.id !== card.id) {
    autosaveCard.flush()
  }
  if (prev && card && prev.id === card.id && gameIdRef.value) {
    autosaveCard(gameIdRef.value, card.id)
  }
}, { deep: true })

// Title/subtitle changes re-persist the definition, debounced.
const autosaveDefinition = debounce(() => {
  saveDefinition().catch(e => { errorRef.value = `Couldn't save the game: ${(e as Error).message}` })
}, 700)
watch([titleRef, subtitleRef], () => {
  if (gameIdRef.value) {
    autosaveDefinition()
  }
})

// Heal ported images: a card ported from the old model carries a legacy `path` and a placeholder
// image id with no blob. Re-upload the path's content (the server assigns a real id), swap it in, and
// drop the path. A failed upload keeps the path so a later pass can retry, and its name is returned
// rather than swallowed -- an unreported failure is how large images once ended up referenced in the
// card but never copied to a blob. Returns whether the card changed (needs saving) and the failures.
async function healImages(card: GameCardInfo) {
  const gameId = gameIdRef.value
  const failed: string[] = []
  let changed = false
  if (!gameId || !card.images) {
    return { changed, failed }
  }
  for (const image of card.images) {
    if (!image.path) {
      continue
    }
    const name = image.path.split('/').pop() ?? image.path
    const blob = await fetch(gamesApi.imagePathUrl(gameId, image.path)).then(r => (r.ok ? r.blob() : null)).catch(() => null)
    if (!blob) {
      failed.push(name)
      continue // leave the path so a later pass can retry
    }
    try {
      const result = await gamesApi.addImage(gameId, blob, name)
      image.imageId = result.imageId
      image.path = undefined
      changed = true
    } catch {
      failed.push(name) // leave the path so a later pass can retry
    }
  }
  return { changed, failed }
}

// Repair every card's ported images once, when the game loads. Uploads that failed under an older
// server size limit are retried here; a healed card is saved explicitly (the autosave watch only
// covers the selected card), and any image that still fails is surfaced rather than left silent.
async function healAllCards() {
  const gameId = gameIdRef.value
  if (!gameId) {
    return
  }
  const failed: string[] = []
  for (const card of cardsRef.value) {
    const result = await healImages(card)
    failed.push(...result.failed)
    if (result.changed) {
      await gamesApi.putCardInfo(gameId, card.id, card)
    }
  }
  if (failed.length) {
    errorRef.value = `Couldn't upload ${failed.length} image(s): ${failed.join(', ')}. They may be too large. Reopen the game to retry.`
  }
}

// Reset per-card UI state (image selection, detail select mode) when switching cards.
watch(selectedCardIdRef, () => {
  selectedImageIdRef.value = undefined
  selectModeRef.value = false
  selectedDetailsRef.value = new Set()
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKeyDown)
  autosaveCard.flush()
  autosaveDefinition.flush()
})

function openNewCard() {
  errorRef.value = ''
  if (!tokenRef.value.trim()) {
    errorRef.value = 'Enter an FS token before adding a card.'
    return
  }
  newCardModalRef.value?.open()
}

async function onAddCard(personId: string) {
  const gameId = gameIdRef.value
  const token = tokenRef.value.trim()
  if (!gameId) {
    return
  }
  if (!token || !personId) {
    errorRef.value = 'Enter an FS token and a person id.'
    newCardModalRef.value?.reset()
    return
  }
  errorRef.value = ''
  try {
    const source = await readGameSourcePerson(personId, token)
    const birthPlace = source.details?.find(d => d.type === 'Birth')?.place
    const card = await gamesApi.addCardInfo(gameId, {
      sourceType: 'FS',
      sourcePersonId: personId,
      name: source.name,
      details: source.details,
      relationship: '',
      flag: CountryCodes.fromName(GeoUtil.getCountryName(birthPlace))?.toLowerCase(),
      childOrder: source.childOrder,
      siblingsCount: source.siblingsCount,
      childCount: source.childCount,
      images: [],
    })
    cardsRef.value.push(card)
    galleryByCardIdRef.value[card.id] = source.images
    selectedCardIdRef.value = card.id
    await saveDefinition()
    newCardModalRef.value?.close()
  } catch (e) {
    errorRef.value = `Couldn't add the card: ${(e as Error).message}`
    newCardModalRef.value?.reset()
  }
}

// Link a ported card that lacks an FS person id: set it (and mark the source FS) so the card can read
// from FamilySearch. The input shows only while the id is absent -- an established linkage is read-only.
function setPersonId(event: Event) {
  const card = selectedCardRef.value
  const value = (event.target as HTMLInputElement).value.trim().toUpperCase()
  if (card && value) {
    card.sourceType = 'FS'
    card.sourcePersonId = value
  }
}

// Re-reads the person from FamilySearch and refreshes the card's name/details/gallery in
// place -- the deep watch on the selected card auto-saves the refreshed values.
async function readCardFromFamilySearch() {
  const card = selectedCardRef.value
  const personId = selectedPersonIdRef.value
  const token = tokenRef.value.trim()
  if (!card || !personId) {
    return
  }
  if (!token) {
    errorRef.value = 'Enter an FS token.'
    return
  }
  savingRef.value = true
  errorRef.value = ''
  try {
    const source = await readGameSourcePerson(personId, token)
    card.name = source.name
    card.details = source.details
    card.childOrder = source.childOrder ?? card.childOrder
    card.siblingsCount = source.siblingsCount ?? card.siblingsCount
    card.childCount = source.childCount ?? card.childCount
    galleryByCardIdRef.value[card.id] = source.images
  } catch (e) {
    errorRef.value = `Couldn't read from FamilySearch: ${(e as Error).message}`
  } finally {
    savingRef.value = false
  }
}

// A cleared number field yields an empty value -- store undefined rather than NaN.
function setNumber(field: 'childOrder' | 'siblingsCount' | 'childCount', event: Event) {
  const card = selectedCardRef.value
  if (!card) {
    return
  }
  const raw = (event.target as HTMLInputElement).value
  card[field] = raw === '' ? undefined : Number(raw)
}

// Order life events chronologically -- dateless details sort to the end.
function detailSortValue(detail: GameCardDetail) {
  return parseDate(detail.date)?.sortValue ?? Number.MAX_SAFE_INTEGER
}

// The card's age at a detail's date -- birth year to this detail's year, when both are known.
function detailAge(detail: GameCardDetail) {
  const birthYear = parseDate(selectedCardRef.value?.details?.find(d => d.type === 'Birth')?.date)?.year
  const year = parseDate(detail.date)?.year
  return birthYear !== undefined && year !== undefined ? year - birthYear : undefined
}

// relationship path from the root person: F (father) = blue square, M (mother) = pink square.
function relationshipSquares(relationship?: string) {
  return [...(relationship ?? '')].map(step => (step === 'F' ? 'f' : 'm'))
}

function openEditDetail(detail: GameCardDetail) {
  editDetailRef.value = detail
  editDetailModalRef.value?.open({ ...detail })
}

// A new detail is added rather than edited -- signalled by a cleared reference.
function openAddDetail() {
  editDetailRef.value = undefined
  editDetailModalRef.value?.open({ type: '' })
}

// Add a new detail or replace the edited one in place -- the deep watch auto-saves it. The list
// is displayed date-sorted, so edits target the detail by reference rather than a display index.
function onSaveDetail(updated: GameCardDetail) {
  const card = selectedCardRef.value
  if (card) {
    if (!card.details) {
      card.details = []
    }
    const index = editDetailRef.value ? card.details.indexOf(editDetailRef.value) : -1
    if (index >= 0) {
      card.details.splice(index, 1, updated)
    } else {
      card.details.push(updated)
    }
  }
  editDetailModalRef.value?.close()
}

function onDeleteDetail() {
  const card = selectedCardRef.value
  const index = editDetailRef.value ? (card?.details?.indexOf(editDetailRef.value) ?? -1) : -1
  if (card?.details && index >= 0) {
    card.details.splice(index, 1)
  }
  editDetailModalRef.value?.close()
}

function toggleSelectMode() {
  selectModeRef.value = !selectModeRef.value
  selectedDetailsRef.value = new Set()
}

function toggleDetailSelected(detail: GameCardDetail) {
  const selected = new Set(selectedDetailsRef.value)
  if (selected.has(detail)) {
    selected.delete(detail)
  } else {
    selected.add(detail)
  }
  selectedDetailsRef.value = selected
}

function toggleSelectAll() {
  selectedDetailsRef.value = allDetailsSelectedRef.value ? new Set() : new Set(selectedCardRef.value?.details ?? [])
}

// Drop the selected details in one pass (no per-row confirm -- they're re-importable from FS).
function deleteSelectedDetails() {
  const card = selectedCardRef.value
  if (card?.details) {
    card.details = card.details.filter(detail => !selectedDetailsRef.value.has(detail))
  }
  selectedDetailsRef.value = new Set()
}

async function openAddImage() {
  imageGalleryModalRef.value?.open()
  await ensureGallery()
}

// For an FS-sourced card whose gallery hasn't been loaded this session, fetch just the memories --
// not the person's facts -- so the picker isn't empty without re-reading (and clobbering) the card.
async function ensureGallery() {
  const card = selectedCardRef.value
  const personId = selectedPersonIdRef.value
  const token = tokenRef.value.trim()
  if (!card || !personId || !token || card.id in galleryByCardIdRef.value) {
    return
  }
  loadingGalleryRef.value = true
  try {
    galleryByCardIdRef.value[card.id] = await readGameSourceImages(personId, token)
  } catch (e) {
    imageGalleryModalRef.value?.showError(`Couldn't load FamilySearch memories: ${(e as Error).message}`)
  } finally {
    loadingGalleryRef.value = false
  }
}

async function onSelectImage(selection: { source: SourceImage } | { file: File }) {
  const gameId = gameIdRef.value
  const card = selectedCardRef.value
  if (!gameId || !card) {
    return
  }
  try {
    let imageId: string
    if ('file' in selection) {
      const result = await gamesApi.addImage(gameId, selection.file, selection.file.name)
      imageId = result.imageId
    } else {
      // FS may CORS-block the original-bytes fetch -- surface a clear, actionable message.
      let blob: Blob
      try {
        blob = await (await fetch(selection.source.originalUrl)).blob()
      } catch {
        imageGalleryModalRef.value?.showError("Couldn't fetch this image from FamilySearch — try downloading and uploading it as a file.")
        return
      }
      const result = await gamesApi.addImage(gameId, blob, selection.source.filename)
      imageId = result.imageId
    }
    if (!card.images) {
      card.images = []
    }
    card.images.push({ imageId, focusX: 0.5, focusY: 0.5, zoom: 1 })
  } catch (e) {
    imageGalleryModalRef.value?.showError(`Couldn't add the image: ${(e as Error).message}`)
  }
}

// Each tile is a click-to-select inline cropper. Once selected, drag pans and Ctrl+wheel zooms toward
// the cursor -- both act on the selected image by id (not DOM focus), so the reorder buttons stay
// clickable without dropping the selection. A plain wheel scrolls the page. Mutating the image object
// triggers the deep-watch auto-save.
let dragImage: GameCardImage | null = null
let dragTile: HTMLElement | null = null
let dragImg: HTMLImageElement | null = null
let dragLastX = 0
let dragLastY = 0
let dragTravel = 0

function onTileDown(event: PointerEvent, image: GameCardImage) {
  if (event.button !== 0) {
    // ignore right/middle click -- leave the context menu alone
    return
  }
  const tile = event.currentTarget as HTMLElement
  tile.setPointerCapture(event.pointerId)
  dragImage = image
  dragTile = tile
  dragImg = tile.querySelector('img')
  dragLastX = event.clientX
  dragLastY = event.clientY
  dragTravel = 0
}

function onTileMove(event: PointerEvent, image: GameCardImage) {
  if (dragImage !== image || event.buttons === 0) {
    return
  }
  const dx = event.clientX - dragLastX
  const dy = event.clientY - dragLastY
  dragLastX = event.clientX
  dragLastY = event.clientY
  dragTravel += Math.abs(dx) + Math.abs(dy)
  // Only the selected tile crops -- keep accumulating travel so an unselected tile still clicks.
  const selected = selectedImageIdRef.value === image.imageId
  if (!selected || !dragTile || !dragImg?.naturalWidth) {
    return
  }
  const focal = { focusX: image.focusX ?? 0.5, focusY: image.focusY ?? 0.5, zoom: image.zoom ?? 1 }
  const next = panFocal(focal, dx, dy, dragImg.naturalWidth, dragImg.naturalHeight, dragTile.clientWidth, dragTile.clientHeight)
  image.focusX = next.focusX
  image.focusY = next.focusY
}

function onTileUp(event: PointerEvent, image: GameCardImage) {
  if (dragImage === image) {
    // A press that barely moved is a click -> toggle selection (drag and Ctrl+wheel then act on it).
    if (dragTravel < 4) {
      selectedImageIdRef.value = selectedImageIdRef.value === image.imageId ? undefined : image.imageId
    }
    if (dragTile?.hasPointerCapture(event.pointerId)) {
      dragTile.releasePointerCapture(event.pointerId)
    }
  }
  dragImage = null
  dragTile = null
  dragImg = null
}

function onTileWheel(event: WheelEvent, image: GameCardImage) {
  // Ctrl+wheel (also a trackpad pinch) zooms the selected tile; a plain wheel scrolls the page.
  const selected = selectedImageIdRef.value === image.imageId
  if (!selected || !event.ctrlKey) {
    return
  }
  const tile = event.currentTarget as HTMLElement
  const img = tile.querySelector('img')
  if (!img?.naturalWidth) {
    return
  }
  event.preventDefault()
  const factor = event.deltaY < 0 ? 1.1 : 1 / 1.1
  // Cursor in the tile's content box (clientLeft/Top strip the border) so it matches v-focal.
  const rect = tile.getBoundingClientRect()
  const px = event.clientX - rect.left - tile.clientLeft
  const py = event.clientY - rect.top - tile.clientTop
  const focal = { focusX: image.focusX ?? 0.5, focusY: image.focusY ?? 0.5, zoom: image.zoom ?? 1 }
  const next = zoomFocalToward(focal, focal.zoom * factor, px, py, img.naturalWidth, img.naturalHeight, tile.clientWidth, tile.clientHeight)
  image.focusX = next.focusX
  image.focusY = next.focusY
  image.zoom = next.zoom
}

// Delete removes the selected image, unless a text field has focus (Delete edits text there).
function onKeyDown(event: KeyboardEvent) {
  if (event.key !== 'Delete' || !selectedImageIdRef.value) {
    return
  }
  const active = document.activeElement?.tagName
  if (active === 'INPUT' || active === 'TEXTAREA') {
    return
  }
  deleteSelectedImage()
}

async function deleteSelectedImage() {
  const gameId = gameIdRef.value
  const card = selectedCardRef.value
  const imageId = selectedImageIdRef.value
  const removeAt = card?.images?.findIndex(x => x.imageId === imageId) ?? -1
  if (!gameId || !card || !imageId || removeAt < 0) {
    return
  }
  try {
    await gamesApi.deleteImage(gameId, imageId)
    card.images?.splice(removeAt, 1)
    selectedImageIdRef.value = undefined
  } catch (e) {
    errorRef.value = `Couldn't remove the image: ${(e as Error).message}`
  }
}

// Reorder the selected image within the card's gallery -- the first image is the primary (the card's
// face). Drag is reserved for cropping, so ordering is done with these explicit controls.
function moveSelectedImage(delta: number) {
  const images = selectedCardRef.value?.images
  const from = selectedImageIndexRef.value
  const to = from + delta
  if (!images || from < 0 || to < 0 || to >= images.length) {
    return
  }
  const [image] = images.splice(from, 1)
  images.splice(to, 0, image)
}

function makeSelectedPrimary() {
  const images = selectedCardRef.value?.images
  const from = selectedImageIndexRef.value
  if (!images || from <= 0) {
    return
  }
  const [image] = images.splice(from, 1)
  images.unshift(image)
}

async function deleteSelectedCard() {
  const gameId = gameIdRef.value
  const card = selectedCardRef.value
  if (!gameId || !card) {
    return
  }
  const confirmed = await confirmDelete.ask()
  if (!confirmed) {
    return
  }
  savingRef.value = true
  errorRef.value = ''
  try {
    // Cancel any pending card save so it can't re-PUT the card we're about to delete.
    autosaveCard.cancel()
    await gamesApi.deleteCardInfo(gameId, card.id)
    cardsRef.value = cardsRef.value.filter(c => c.id !== card.id)
    delete galleryByCardIdRef.value[card.id]
    selectedCardIdRef.value = undefined
    await saveDefinition()
  } catch (e) {
    errorRef.value = `Couldn't delete the card: ${(e as Error).message}`
  } finally {
    savingRef.value = false
  }
}
</script>

<style lang="scss" scoped>
.game-builder-page {
  padding: 1rem;

  .page-title {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .token {
    display: flex;
    flex-direction: column;

    input {
      width: 300px;
      font-family: monospace;
    }
  }

  .game-fields {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 1rem;
    margin-top: 1rem;

    label {
      display: flex;
      flex-direction: column;
    }
  }

  .error {
    margin-top: 1rem;
    color: #000;
    font-size: 0.875rem;
  }

  .editor {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1rem;

    @media (min-width: 768px) {
      flex-direction: row;
      align-items: flex-start;
    }
  }

  .cards {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .cards-count {
      font-size: 0.875em;
      color: #aaa;
    }

    @media (min-width: 768px) {
      width: 240px;
      flex-shrink: 0;
    }

    .card-item {
      display: flex;
      // buttons default to text-align: center -- keep the stretched name (and squares) left-aligned.
      text-align: left;
      align-items: stretch;
      position: relative;
      height: 40px;
      padding: 0;
      overflow: hidden;
      border: 1px solid #eee;
      border-radius: 0;
      background: #fff;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      cursor: pointer;

      .card-thumb {
        flex: 0 0 36px;
        position: relative;
        overflow: hidden;
        background: #f4f4f4;

        img {
          // sizing and placement are applied by v-focal
          user-select: none;
        }
      }

      .card-info {
        --rel-square-size: 7px;
        align-self: center;
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 0 0.5rem;
      }

      .card-name {
        font-size: 0.875rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .card-flag {
        position: absolute;
        right: 3px;
        bottom: 3px;
        font-size: 10px;
      }

      &.selected {
        background: var(--bs-primary-bg-subtle);
      }
    }

    .new-card {
      align-self: flex-start;
    }
  }

  // relationship path from the root person: F (father) = blue square, M (mother) = pink square,
  // matching MatchingImageCard.vue. --rel-square-size lets each context size them (12px default).
  .rel-squares {
    display: flex;
    gap: 2px;
  }

  .rel-square {
    width: var(--rel-square-size, 12px);
    height: var(--rel-square-size, 12px);
    border-radius: 2px;

    &.f {
      background: #73a7f0;
    }

    &.m {
      background: #f2c1d6;
    }
  }

  .card-editor {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex: 1;
    max-width: 600px;
  }

  .person-id {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #888;

    .value {
      color: #000;
      font-family: monospace;
    }

    .person-id-input {
      width: 14rem;
    }
  }

  .fields {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .field-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .field-notes {
      align-items: flex-start;
    }

    .field-label {
      width: 7rem;
      flex-shrink: 0;
      margin-bottom: 0;
    }

    .field-hint {
      color: #888;
      font-size: 0.875rem;
    }

    .field-name {
      width: 18rem;
    }

    .field-relationship {
      width: 100px;
    }

    .field-wide {
      flex: 1;
    }

    .field-number {
      width: 4rem;
    }

    .field-flag {
      width: 80px;
    }

    .fi {
      font-size: 1.5rem;
    }

    .flag-country {
      color: #888;
      font-size: 0.875rem;
    }
  }

  .details {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    font-size: 0.875rem;

    .details-side {
      width: 7rem;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .details-label {
      color: #888;
    }

    .details-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .select-toggle {
      padding: 0;
      font-size: 0.875rem;
    }

    .delete-selected {
      display: flex;
      align-items: center;
      padding: 0;
      color: var(--bs-danger);

      svg {
        width: 1rem;
        height: 1rem;
      }
    }

    .details-list {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .select-all {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #888;
      font-size: 0.75rem;
    }
  }

  .detail {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;

    &.highlight {
      padding: 0.125rem 0;
      background: #fff176;
    }
  }

  .detail-check {
    flex-shrink: 0;
    margin-top: 0.125rem;
  }

  .detail-date {
    width: 8rem;
    color: #000;
    text-align: right;
  }

  .detail-age {
    width: 2rem;
    color: #aaa;
    text-align: center;
  }

  .detail-body {
    display: flex;
    flex-direction: column;
  }

  .detail-type {
    padding: 0;
    text-align: left;
    font-size: 0.875rem;
  }

  .detail-place {
    color: #888;
    font-size: 0.75rem;
  }

  .detail-flag {
    margin-left: auto;
    font-size: 0.875rem;
  }

  .add-detail {
    align-self: flex-start;
    padding: 0;
    font-size: 0.875rem;
  }

  .images {
    --thumb-size: 106px;
    --tile-gap: 0.5rem;

    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: var(--tile-gap);
  }

  .image-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;

    .btn-link {
      display: flex;
      align-items: center;
      padding: 0;
      font-size: 0.875rem;

      svg {
        width: 1.25rem;
        height: 1.25rem;
      }
    }
  }

  .thumbnails {
    display: grid;
    grid-template-columns: repeat(3, var(--thumb-size));
    gap: var(--tile-gap);
  }

  // each tile is a fixed-size, overflow-hidden frame -- v-focal sizes and places the img.
  .tile {
    position: relative;
    width: var(--thumb-size);
    height: var(--thumb-size);
    padding: 0;
    overflow: hidden;
    border: 1px solid #ccc;
    border-radius: 12px;
    background: none;
    cursor: pointer;

    img {
      // sizing and placement are applied by v-focal. draggable=false plus these stop the browser's
      // native image-drag from hijacking the pan gesture (notably in Firefox).
      user-select: none;
      -webkit-user-drag: none;
    }

    &.primary {
      width: 256px;
      height: calc(2 * var(--thumb-size) + var(--tile-gap));
    }

    // the focused (selected) tile shows a thin primary border (inset so it doesn't resize the frame
    // v-focal measures) and takes crop gestures -- drag to pan, Ctrl+scroll to zoom.
    // suppress the browser's default focus ring -- selection is shown by .selected instead.
    &:focus {
      outline: none;
    }

    // the selected tile shows a thick primary ring (an outset shadow so it sits outside the frame
    // and isn't painted over by the image) and takes crop gestures -- drag to pan, Ctrl+scroll to zoom.
    &.selected {
      cursor: grab;
      touch-action: none;
      box-shadow: 0 0 0 3px var(--bs-primary);

      &:active {
        cursor: grabbing;
      }
    }

    // the add tile: a subtle dashed frame with a centred plus icon.
    &.add {
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px dashed #ccc;
      color: var(--bs-primary);

      svg {
        width: 32px;
        height: 32px;
      }

      &.small svg {
        width: 24px;
        height: 24px;
      }
    }
  }

  .danger-zone {
    margin-top: 1rem;
  }
}
</style>
